import { Button } from '@components/Button'
import { PublicSectionGate } from '@components/PublicPage'
import { Card, Foot, Panel, PanelTitle, SignInPanel, SignInShell } from '@components/SignInCard'
import Spinner from '@components/Spinner'
import { useAuth } from '@context/AuthContext'
import { useJoinPreview } from '@hooks/useJoinPreview'
import { useJoinTrip } from '@hooks/useJoinTrip'
import { useTrips } from '@hooks/useTrips'
import { getErrorMessage, isConflict } from '@lib/apiError'
import { tripJoinUrl } from '@lib/joinLink'
import { queryKeys } from '@lib/queryKeys'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import InvitationPanel from './InvitationPanel'
import {
	buildJoinInvitation,
	deriveJoinAction,
	deriveMembership,
	joinMethodFromSearch,
	JoinPreview,
} from './core'
import { AppLink, BackButton, Checking, AttemptError, PrimaryButton, SignOutButton, StatusLabel } from './JoinTripPage.styles'

/**
 * The Join page at `/join/:tripCode` — what a Join link resolves to on the web.
 * Standalone (no app Layout): the login card with the invitation as its hero.
 * A signed-out visitor signs in on the spot; once there is a session the page
 * joins them and lands them on the trip — opening a Join link is the consent,
 * there is no Join button. Someone already in the trip gets "Open trip".
 */
type JoinAttempt =
	| { kind: 'idle' }
	| { kind: 'joining' }
	/** The server answered 409 and the membership re-check could not confirm it. */
	| { kind: 'already-in' }
	| { kind: 'failed'; message: string }

const JoinTripPage: React.FC = () => {
	const { tripCode } = useParams<{ tripCode: string }>()
	const location = useLocation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { token, user, isLoading: authLoading, logout } = useAuth()
	const signedIn = !!token

	const preview = useJoinPreview(tripCode)
	const tripsQuery = useTrips({ enabled: signedIn })
	const joinTrip = useJoinTrip()
	const method = joinMethodFromSearch(location.search)

	const { status: membership, memberTrip } = deriveMembership({
		signedIn,
		isLoading: tripsQuery.isLoading,
		trips: tripsQuery.data?.trips,
		tripCode,
	})
	const action = authLoading ? 'checking' : deriveJoinAction({ signedIn, membership })

	// Signing in clears the whole query cache (a new session must never see the
	// previous account's data), which would blank the invitation into a loading
	// screen for a beat. Keep the last preview on screen while it refills.
	const lastPreview = useRef<JoinPreview | undefined>(undefined)
	if (preview.data) lastPreview.current = preview.data
	const gateQuery =
		preview.isLoading && lastPreview.current
			? { data: lastPreview.current, isLoading: false, error: undefined }
			: preview

	const [attempt, setAttempt] = useState<JoinAttempt>({ kind: 'idle' })

	const join = async (code: string) => {
		setAttempt({ kind: 'joining' })
		try {
			const trip = await joinTrip.mutateAsync({ code, method })
			toast.success('Successfully joined the trip!')
			navigate(`/trips/${trip.id}`)
		} catch (error) {
			if (isConflict(error)) {
				// Already in — they joined from the phone in between. Re-check
				// membership so the page can offer "Open trip" for the right one.
				await queryClient.refetchQueries({ queryKey: queryKeys.trips() })
				setAttempt({ kind: 'already-in' })
				return
			}
			setAttempt({ kind: 'failed', message: getErrorMessage(error, 'Failed to join trip. Please try again.') })
		}
	}

	// Join the moment the page knows it can: a session, no membership yet, and a
	// valid invitation. Once only — a retry is the person's click, not a re-run.
	const hasInvitation = !!(preview.data ?? lastPreview.current)
	const autoJoinFired = useRef(false)
	useEffect(() => {
		if (action !== 'join' || !hasInvitation || !tripCode || autoJoinFired.current) return
		autoJoinFired.current = true
		void join(tripCode)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [action, hasInvitation, tripCode])

	return (
		<PublicSectionGate
			query={gateQuery}
			loadingLabel="Loading invitation…"
			unavailableTitle="This invitation has expired"
			unavailableMessage="This trip link is no longer valid. Ask the trip organizer to send you a fresh invitation."
			action={
				signedIn ? (
					<Button as={Link} to="/trips" variant="secondary">
						Back to my trips
					</Button>
				) : undefined
			}
		>
			{data => {
				const invitation = buildJoinInvitation(data)
				return (
					<SignInShell>
						<Card>
							<InvitationPanel invitation={invitation} />

							{action === 'sign-in' && (
								<SignInPanel title="Sign in to join" showAppFoot={false}>
									<AppLink href={tripJoinUrl(data.tripInfo.code)}>
										Have the app? <b>Open it there</b>
									</AppLink>
								</SignInPanel>
							)}

							{action === 'checking' && (
								<Panel>
									<Checking>
										<Spinner size={36} />
									</Checking>
								</Panel>
							)}

							{action === 'join' && (attempt.kind === 'idle' || attempt.kind === 'joining') && (
								<Panel>
									<Checking>
										<Spinner size={36} />
										<StatusLabel>Joining {data.tripInfo.name}…</StatusLabel>
									</Checking>
								</Panel>
							)}

							{action === 'join' && attempt.kind === 'failed' && (
								<Panel>
									<PanelTitle>Couldn&rsquo;t join this trip</PanelTitle>
									<AttemptError role="alert">{attempt.message}</AttemptError>
									<PrimaryButton type="button" onClick={() => tripCode && join(tripCode)}>
										Try again
									</PrimaryButton>
									<BackButton type="button" onClick={() => navigate('/trips')}>
										Back to my trips
									</BackButton>
									<Foot>
										Signed in as {user?.firstName || user?.email}. Not you?{' '}
										<SignOutButton type="button" onClick={logout}>
											Sign out
										</SignOutButton>
									</Foot>
								</Panel>
							)}

							{action === 'join' && attempt.kind === 'already-in' && (
								<Panel>
									<PanelTitle>You are already in this trip</PanelTitle>
									<PrimaryButton type="button" onClick={() => navigate('/trips')}>
										Go to my trips
									</PrimaryButton>
								</Panel>
							)}

							{action === 'open' && memberTrip && (
								<Panel>
									<PanelTitle>You are already in this trip</PanelTitle>
									<PrimaryButton type="button" onClick={() => navigate(`/trips/${memberTrip.id}`)}>
										Open trip
									</PrimaryButton>
								</Panel>
							)}
						</Card>
					</SignInShell>
				)
			}}
		</PublicSectionGate>
	)
}

export default JoinTripPage
